import { CSS } from "@dnd-kit/utilities"
import React, { useCallback, useRef } from "react"

import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from "./entity-table.utils"
import { useReorderableHeader } from "./use-reorderable-header"

export interface EntityTableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  columnKey?: string
  reorderable?: boolean
  // Floor for this column specifically. A column declared narrower than
  // MIN_COLUMN_WIDTH (a row-number or checkbox column) must be allowed down to
  // its own size; anything wider keeps the shared floor.
  minWidth?: number
  onColumnResize?: (key: string, width: number) => void
  onResizeStart?: () => void
}

export const EntityTableHeaderCell: React.FC<EntityTableHeaderCellProps> = ({
  columnKey,
  reorderable = false,
  minWidth,
  onColumnResize,
  onResizeStart,
  children,
  style,
  ...rest
}) => {
  const { setNodeRef, dragProps, isDragging, transform, transition } =
    useReorderableHeader(columnKey, rest.onKeyDown, reorderable)
  const thRef = useRef<HTMLTableCellElement | null>(null)

  const setRef = useCallback(
    (node: HTMLTableCellElement | null) => {
      thRef.current = node
      setNodeRef(node)
    },
    [setNodeRef]
  )

  const handleResizeStart = (event: React.PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!columnKey || !onColumnResize || !thRef.current) return
    const th = thRef.current
    onResizeStart?.()
    const startX = event.clientX
    const startWidth = th.getBoundingClientRect().width
    const onMove = (moveEvent: PointerEvent) => {
      const next = Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(
          minWidth ?? MIN_COLUMN_WIDTH,
          Math.round(startWidth + moveEvent.clientX - startX)
        )
      )
      onColumnResize(columnKey, next)
    }
    const onUp = () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
      // A resize drag that ends over the header still makes the browser fire a
      // `click` on the <th>, which AntD's sortable header reads as a sort toggle.
      // Swallow that one trailing click so resizing never changes the sort order.
      const suppressClick = (clickEvent: Event) => {
        clickEvent.stopPropagation()
        th.removeEventListener("click", suppressClick, true)
      }
      th.addEventListener("click", suppressClick, true)
      window.setTimeout(
        () => th.removeEventListener("click", suppressClick, true),
        0
      )
    }
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
  }

  // Keyless cells (e.g. a fixed:"right" actions column) keep AntD's own style
  // untouched so its sticky positioning isn't clobbered. A keyed cell needs
  // `position: relative` to anchor the resize handle — that is required even
  // when the column cannot be dragged, so it is separate from the drag styling.
  const resizable = columnKey != null && !!onColumnResize

  const mergedStyle: React.CSSProperties | undefined =
    reorderable || resizable
      ? {
          ...style,
          position: style?.position ?? "relative",
          ...(reorderable
            ? {
                transform: CSS.Translate.toString(transform),
                transition,
                cursor: "grab",
                touchAction: "none",
                ...(isDragging ? { opacity: 0.6, zIndex: 2 } : {})
              }
            : {})
        }
      : style

  return (
    <th
      ref={setRef}
      data-col-key={columnKey}
      style={mergedStyle}
      {...rest}
      {...dragProps}
    >
      {children}
      {columnKey && onColumnResize ? (
        <span
          className="entity-table-resize-handle"
          data-testid={`resize-handle-${columnKey}`}
          onPointerDown={handleResizeStart}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 0,
            right: -4,
            height: "100%",
            width: 8,
            cursor: "col-resize",
            userSelect: "none",
            touchAction: "none",
            zIndex: 1
          }}
          aria-hidden="true"
        />
      ) : null}
    </th>
  )
}
